import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import axios from "axios";
import Loader from "../sub-components/Laoder";
import { useUser } from "../../context/UserContext";
import logo from "../../Assets/logo.png";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useUser();

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      try {
        await handleLogin(values);
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Login API integration
  const handleLogin = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/login`,
        {
          email,
          password,
          role: "user",
        }
      );

      if (response.status === 200) {
        toast.success(response.data.message || "Login successful!");
        console.log(response.data.user);
        sessionStorage.setItem("user", JSON.stringify(response.data.user));
        setUser(response.data.user);
        navigate("/dashboard");
      } else if (response.status === 400 || response.status === 404) {
        toast.error(response.data.message || "Invalid credentials!");
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(
          error.response.data.message || "An error occurred during login."
        );
      } else {
        toast.error("Unable to connect to the server. Please try again.");
      }
    }
  };

  return (
    <div className="login_bg_img flex justify-center items-center">
      <div className="w-screen h-screen flex justify-center items-center bg_blure">
        <div className="login_container w-[993px] h-[699px] flex items-center justify-center flex-col p-16">
          <div className="title flex items-center justify-center flex-col mt-[20px]">
            <img
              src={logo}
              onClick={() => {
                navigate("/");
              }}
              className="p-4 cursor-pointer"
              alt="logo"
            />
            <h1 className="text-center text-white text-4xl font-semibold mb-[12px]">
              Welcome Back
            </h1>
            <p className="text-center text-white text-base max-w-[316px]">
              To keep connected with us, please enter your login details.
            </p>
          </div>

          <div className="details flex items-center justify-center mt-[40px] w-[398px]">
            <form
              onSubmit={formik.handleSubmit}
              className="w-[290px] md:w-[450px]"
            >
              {/* Email Input */}
              <div className="mb-4">
                <input
                  type="email"
                  id="email"
                  className="email  bg-transparent focus:outline-none w-full py-2 px-3 leading-tight light-white"
                  placeholder="Enter your email"
                  {...formik.getFieldProps("email")}
                  aria-invalid={!!formik.errors.email && formik.touched.email}
                />
                {formik.touched.email && formik.errors.email && (
                  <div className="text-red-500 error-message">
                    {formik.errors.email}
                  </div>
                )}
              </div>

              {/* Password Input */}
              <div className="mb-4 relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  className="password bg-transparent focus:outline-none w-full py-2 px-3 leading-tight light-white"
                  placeholder="Enter your password"
                  {...formik.getFieldProps("password")}
                  aria-invalid={
                    !!formik.errors.password && formik.touched.password
                  }
                />
                <span
                  onClick={togglePasswordVisibility}
                  className="absolute right-[10px] top-[10px] text-white cursor-pointer"
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </span>
                {formik.touched.password && formik.errors.password && (
                  <div className="text-red-500 error-message">
                    {formik.errors.password}
                  </div>
                )}
              </div>

              <p className="text-md text-right text-white cursor-pointer mb-4">
                <Link to="/password">Forgot Password?</Link>
              </p>

              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-[24px] w-full"
              >
                {isLoading ? <Loader isLoading={isLoading} /> : "Login"}
              </button>

              <p className="text-md text-center text-white mt-4">
                Don’t have an account?&nbsp;
                <Link to="/signup" className="font-bold cursor-pointer">
                  Signup
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
